import React from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined'

import Link from '@/components/Common/Link'
import Button from '@/components/Common/Button'
import ClampText from '@/components/ClampText'
import COLORS from '@/src/colors'

export const CARD_WIDTH = 180

export type ProductGridCardProps = {
  productId: string
  name: string
  image: string
  brandName?: string
  code?: string
  price?: string
  href?: string
  as?: string
  loading?: boolean
  deleting?: boolean
  onDelete?: () => void
  onAddToCart?: () => void
  onMoveToCart?: () => void
}

const ProductGridCard: React.FC<ProductGridCardProps> = ({
  productId,
  name,
  image,
  brandName,
  code,
  price,
  href,
  as: asUrl,
  loading,
  deleting,
  onDelete,
  onAddToCart,
  onMoveToCart,
}) => {
  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 90,
          bgcolor: '#fafafa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: 1,
          flexShrink: 0,
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={image || '/images/no-image.png'}
          alt={name || ''}
          width={72}
          height={72}
          style={{ objectFit: 'contain', maxWidth: 72, maxHeight: 72 }}
          onError={(e) => {
            ;(e.target as HTMLImageElement).src = '/images/no-image.png'
          }}
        />
      </Box>
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {!!brandName && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: COLORS.brand,
              fontSize: 11,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {brandName}
          </Typography>
        )}
        <ClampText
          clamp={2}
          style={{ fontWeight: 600, color: '#213390', fontSize: 12, lineHeight: '1.3', marginTop: 2 }}
        >
          {name}
        </ClampText>
        {!!code && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, mt: 0.25, fontStyle: 'italic' }}>
            {code}
          </Typography>
        )}
        {!!price && (
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: COLORS.price, fontSize: 13, mt: 'auto', lineHeight: 1.2, pt: 0.5 }}
          >
            {parseFloat(price).toFixed(2).replace('.', ',')} &euro;
            <span style={{ fontSize: 10, fontWeight: 400 }}> HT</span>
          </Typography>
        )}
      </Box>
    </>
  )

  return (
    <Box
      sx={{
        width: CARD_WIDTH,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
        },
        '&:hover .card-delete-btn': { opacity: 1 },
      }}
    >
      {onDelete && (
        <IconButton
          size="small"
          className="card-delete-btn"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!deleting) onDelete()
          }}
          disabled={deleting}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            opacity: 0.5,
            transition: 'opacity 0.2s',
            bgcolor: 'rgba(255,255,255,0.85)',
            color: 'error.main',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)', opacity: 1 },
            width: 24,
            height: 24,
          }}
        >
          <DeleteForeverOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {href ? (
        <Link
          href={href}
          as={asUrl}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {content}
        </Link>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {content}
        </Box>
      )}

      {(onAddToCart || onMoveToCart) && (
        <Box sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {onAddToCart && (
            <Button
              size="small"
              fullWidth
              loading={loading}
              onClick={onAddToCart}
              startIcon={<ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />}
            >
              Ajouter au panier
            </Button>
          )}
          {onMoveToCart && (
            <Button
              size="small"
              fullWidth
              variant="outlined"
              loading={loading}
              onClick={onMoveToCart}
              startIcon={<MoveToInboxOutlinedIcon sx={{ fontSize: 14 }} />}
            >
              Déplacer vers le panier
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ProductGridCard
